class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.initialMarkup = this.predictiveSearchResults.innerHTML;
    this.instanceKey = 'predictive-' + (++PredictiveSearch.instanceCount);
    this.resultsId = this.instanceKey + '-results';
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.requestId = 0;
    this.searchTerm = '';
    this.isOpen = false;
    this.input.addEventListener('input', () => this.invalidateResults());
    this.input.addEventListener('focus', () => this.onFocus());
    this.input.form.addEventListener('submit', (event) => this.onFormSubmit(event));
    this.addEventListener('focusout', () => {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.resizeHandler = () => { if (this.isOpen) this.updateHeight(); };
  }
  connectedCallback() {
    window.addEventListener('resize', this.resizeHandler);
    window.visualViewport?.addEventListener('resize', this.resizeHandler);
  }
  disconnectedCallback() {
    this.requestId++;
    this.abortController?.abort();
    clearTimeout(this.focusTimer);
    clearTimeout(this.statusTimer);
    window.removeEventListener('resize', this.resizeHandler);
    window.visualViewport?.removeEventListener('resize', this.resizeHandler);
  }
  getQuery() { return this.input.value.trim(); }
  invalidateResults() {
    this.requestId++;
    this.abortController?.abort();
    this.removeAttribute('results');
    this.clearSelection();
    this.predictiveSearchResults.innerHTML = this.initialMarkup;
    if (!this.getQuery()) return this.close();
    this.setAttribute('loading', 'true');
    this.open();
  }
  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (!this.searchTerm) return this.close();
    this.getSearchResults(this.searchTerm);
  }
  onFormReset(event) {
    super.onFormReset(event);
    this.searchTerm = '';
    this.invalidateResults();
    this.close();
  }
  onFocus() {
    const query = this.getQuery();
    if (!query) return;
    if (this.getAttribute('results') === 'true' && this.renderedQuery === query) this.open();
    else this.getSearchResults(query);
  }
  onFormSubmit(event) { if (!this.getQuery()) event.preventDefault(); }
  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.isOpen) return;
      event.preventDefault();
      this.switchOption(event.key === 'ArrowUp' ? -1 : 1);
    } else if (event.key === 'Enter' && this.isOpen) {
      const selected = this.predictiveSearchResults.querySelector('[role="option"][aria-selected="true"]');
      if (!selected) return;
      const action = selected.matches('button') ? selected : selected.querySelector('[data-predictive-result-link], a, button');
      if (action) { event.preventDefault(); action.click(); }
    }
  }
  clearSelection() {
    this.predictiveSearchResults.querySelectorAll('[role="option"][aria-selected="true"]').forEach(e => e.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }
  switchOption(direction) {
    const options = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')].filter(e => e.getClientRects().length && getComputedStyle(e).visibility !== 'hidden');
    if (!options.length) return;
    const current = options.findIndex(e => e.getAttribute('aria-selected') === 'true');
    const next = current < 0 ? (direction > 0 ? 0 : options.length - 1) : (current + direction + options.length) % options.length;
    this.clearSelection();
    options[next].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', options[next].id);
    const panel = this.predictiveSearchResults.getBoundingClientRect();
    const option = options[next].getBoundingClientRect();
    if (option.top < panel.top) this.predictiveSearchResults.scrollTop += option.top - panel.top;
    else if (option.bottom > panel.bottom) this.predictiveSearchResults.scrollTop += option.bottom - panel.bottom;
  }
  async getSearchResults(query) {
    this.abortController?.abort();
    const requestId = ++this.requestId;
    const controller = this.abortController = new AbortController();
    let localizedSearch = /^ar(?:-|$)/i.test(document.documentElement.lang);
    try { localizedSearch ||= JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch === false; } catch (_) {}
    const key = (localizedSearch ? 'localized:' : 'predictive:') + query.toLocaleLowerCase();
    this.setLiveRegionText(this.getAttribute('data-loading-text') || 'Loading');
    this.setAttribute('loading', 'true');
    this.open();
    const deferred = this.dispatchSearchUpdateEvent(query);
    try {
      let markup = PredictiveSearch.cache.get(key);
      if (!markup) {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(localizedSearch ? root + 'search' : window.routes?.predictive_search_url || root + 'search/suggest', location.origin);
        const sectionName = localizedSearch ? 'localized-search-suggestions' : 'predictive-search';
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', sectionName);
        if (localizedSearch) {
          url.searchParams.set('type', 'product');
          url.searchParams.set('options[prefix]', 'last');
        } else {
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[limit_scope]', 'each');
        }
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search suggestions HTTP ' + response.status);
        const html = await response.text();
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-' + sectionName);
        if (!section) throw new Error('Search suggestions response missing section');
        markup = section.innerHTML;
        PredictiveSearch.cache.set(key, markup);
        if (PredictiveSearch.cache.size > 40) PredictiveSearch.cache.delete(PredictiveSearch.cache.keys().next().value);
      }
      if (requestId !== this.requestId || this.getQuery() !== query || !this.isConnected) {
        deferred?.reject(new DOMException('Superseded search', 'AbortError'));
        return;
      }
      this.renderSearchResults(markup, query);
      deferred?.resolve({ totalCount: this.getTotalResultCount() });
    } catch (error) {
      deferred?.reject(error);
      if (error.name === 'AbortError' || requestId !== this.requestId || !this.isConnected) return;
      this.renderError(query);
    }
  }
  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent?.createPromise) return null;
    const deferred = SearchUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: deferred.promise }));
    return deferred;
  }
  renderSearchResults(markup, query) {
    const template = document.createElement('template');
    template.innerHTML = markup;
    const ids = new Map();
    template.content.querySelectorAll('[id]').forEach(e => { const old = e.id; e.id = this.instanceKey + '-' + old; ids.set(old, e.id); });
    template.content.querySelectorAll('[aria-labelledby], [aria-controls], [aria-owns]').forEach(e => {
      for (const attribute of ['aria-labelledby', 'aria-controls', 'aria-owns']) if (e.hasAttribute(attribute)) e.setAttribute(attribute, e.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
    });
    const listbox = template.content.querySelector('[role="listbox"]');
    if (listbox) listbox.id = this.resultsId;
    const term = template.content.querySelector('[data-predictive-query-term]');
    if (term) term.textContent = query;
    this.predictiveSearchResults.replaceChildren(template.content);
    this.renderedQuery = query;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    window.CardModern?.scan(this.predictiveSearchResults);
    this.setLiveRegionText(this.predictiveSearchResults.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
    this.open();
  }
  renderError(query) {
    const box = document.createElement('div');
    box.id = this.resultsId;
    box.setAttribute('role', 'listbox');
    const message = document.createElement('p');
    message.className = 'modern-predictive-feedback';
    message.textContent = this.getAttribute('data-error-text') || 'Suggestions unavailable';
    const button = document.createElement('button');
    button.type = 'submit';
    button.id = this.instanceKey + '-fallback';
    button.className = 'modern-predictive-view-all';
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', 'false');
    button.tabIndex = -1;
    button.textContent = this.getAttribute('data-view-all-text') || 'View all results';
    box.append(message, button);
    this.predictiveSearchResults.replaceChildren(box);
    this.removeAttribute('loading');
    this.setLiveRegionText(message.textContent);
    this.open();
  }
  getTotalResultCount() { return Number(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0; }
  setLiveRegionText(text) {
    if (!this.statusElement) return;
    clearTimeout(this.statusTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    this.statusTimer = setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }
  updateHeight() {
    const viewport = window.visualViewport;
    const bottom = (viewport?.offsetTop || 0) + (viewport?.height || innerHeight);
    const top = this.predictiveSearchResults.getBoundingClientRect().top;
    this.predictiveSearchResults.style.maxHeight = Math.max(100, bottom - Math.max(0, top) - 16) + 'px';
  }
  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.updateHeight();
  }
  close(clearSearchTerm = false) {
    this.requestId++;
    this.abortController?.abort();
    if (clearSearchTerm) { this.input.value = ''; this.removeAttribute('results'); }
    this.clearSelection();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.style.removeProperty('max-height');
    this.isOpen = false;
  }
  closeResults(clearSearchTerm = false) { this.close(clearSearchTerm); }
}
PredictiveSearch.cache = new Map();
PredictiveSearch.instanceCount = 0;
customElements.define('predictive-search', PredictiveSearch);
